import { useForm } from '@tanstack/react-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Classroom } from '@/interface/types/classroom'
import { useTranslation } from 'react-i18next'
import { useUser } from '@/hooks/useUser'

export type ClassroomFormValues = {
  name: string
  type: string
  capacity: number
  floor: string
  building: string
  features?: string
  notes?: string
  isActive: boolean
  etablissementId: string
}

interface ClassroomFormProps {
  initialData?: Partial<Classroom>
  onSubmit: (data: ClassroomFormValues) => Promise<void>
  onCancel: () => void
  submitLabel: string
}

export function ClassroomForm({
  initialData,
  onSubmit,
  onCancel,
  submitLabel,
}: ClassroomFormProps) {
  const { t } = useTranslation('admin')
  const { user } = useUser()
  // Get establishmentId from userStore (fetched from API via HTTP-only cookies)
  const etablissementId = user?.establishmentId || ''

  const form = useForm({
    defaultValues: {
      name: initialData?.name || '',
      type: initialData?.type || '',
      capacity: initialData?.capacity || 0,
      floor: initialData?.floor || '',
      building: initialData?.building || '',
      features: initialData?.features?.join(', ') || '',
      notes: initialData?.notes || '',
      isActive: initialData?.isActive ?? true,
      etablissementId: initialData?.etablissementId || etablissementId,
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value as ClassroomFormValues)
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
      className="space-y-6"
    >
      <div className="grid grid-cols-2 gap-4">
        <form.Field
          name="name"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Name is required'
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="name">
                {t('classroom-folder.classroomForm.fields.name.label')}
              </Label>
              <Input
                id="name"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('classroom-folder.classroomForm.fields.name.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.map((error) =>
                    t(`classroom-folder.classroomForm.fields.name.validation.${error}`)
                  ).join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="type"
          defaultValue='room'
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Type is required'
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="type">
                {t('classroom-folder.classroomForm.fields.type.label')}
              </Label>
              <Input
                id="type"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('classroom-folder.classroomForm.fields.type.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.map((error) =>
                    t(`classroom-folder.classroomForm.fields.type.validation.${error}`)
                  ).join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <form.Field
          name="capacity"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Capacity is required'
              if (value < 1) return 'Capacity must be at least 1'
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="capacity">
                {t('classroom-folder.classroomForm.fields.capacity.label')}
              </Label>
              <Input
                id="capacity"
                type="number"
                value={field.state.value.toString()}
                onBlur={field.handleBlur}
                onChange={(e) =>
                  field.handleChange(parseInt(e.target.value) || 0)
                }
                placeholder={t('classroom-folder.classroomForm.fields.capacity.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.map((error) =>
                    t(`classroom-folder.classroomForm.fields.capacity.validation.${error}`)
                  ).join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="floor"
          defaultValue='1'
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Floor is required'
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="floor">
                {t('classroom-folder.classroomForm.fields.floor.label')}
              </Label>
              <Input
                id="floor"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('classroom-folder.classroomForm.fields.floor.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.map((error) =>
                    t(`classroom-folder.classroomForm.fields.floor.validation.${error}`)
                  ).join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>
      </div>

      <form.Field
        name="building"
        defaultValue='1'
        validators={{
          onChange: ({ value }) => {
            if (!value) return 'Building is required'
            return undefined
          },
        }}
      >
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor="building">
              {t('classroom-folder.classroomForm.fields.building.label')}
            </Label>
            <Input
              id="building"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder={t('classroom-folder.classroomForm.fields.building.placeholder')}
            />
            {field.state.meta.errors && (
              <p className="text-sm text-red-500">
                {field.state.meta.errors.map((error) =>
                  t(`classroom-folder.classroomForm.fields.building.validation.${error}`)
                ).join(', ')}
              </p>
            )}
          </div>
        )}
      </form.Field>

      <form.Field name="features">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor="features">
              {t('classroom-folder.classroomForm.fields.features.label')}
            </Label>
            <Input
              id="features"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder={t('classroom-folder.classroomForm.fields.features.placeholder')}
            />
            <p className="text-xs text-muted-foreground">
              {t('classroom-folder.classroomForm.fields.features.hint')}
            </p>
          </div>
        )}
      </form.Field>

      <form.Field name="notes">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor="notes">
              {t('classroom-folder.classroomForm.fields.notes.label')}
            </Label>
            <Textarea
              id="notes"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder={t('classroom-folder.classroomForm.fields.notes.placeholder')}
            />
          </div>
        )}
      </form.Field>

      <form.Field name="isActive">
        {(field) => (
          <div className="flex flex-row items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label className="text-base">
                {t('classroom-folder.classroomForm.fields.isActive.label')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('classroom-folder.classroomForm.fields.isActive.hint')}
              </p>
            </div>
            <Switch
              checked={field.state.value}
              onCheckedChange={field.handleChange}
            />
          </div>
        )}
      </form.Field>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('classroom-folder.classroomForm.buttons.cancel')}
        </Button>
        <Button type="submit">
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
